import { useEffect, useState } from 'react';
import {
  Card,
  Collapse,
  FormInstance,
  Form,
  Select,
  Input,
  Image,
  Space,
  Button,
  Typography,
} from 'antd';
import { InvertFillButton } from './InvertFillButton';
import { DownloadOutlined } from '@ant-design/icons';
import { statusOption } from '../constants/AppConstant';
import { CreditCard, IdCard, User, Clipboard, CircleCheckBig, Check } from 'lucide-react';

import { InputInterface } from '../utils/forms/FormInterface';
import PhotoUploadModal from './PhotoUploadModal';
import { fallback } from '../constants/FallbackImage';
import { toastError, toastSuccess } from '../service/Toast';
import callApi from '../Api/config';
import { ORDER_USER, PHOTO_ORDER_APPLICATION } from '../constants/EntityName';
import UserDetails from './UserDetails';
import PaymentDetails from './PaymentDetails';
import { getRole } from '../service/CookieManager';
import { BASE_IMAGE_URL } from '../env';

const { Panel } = Collapse;
const { Title, Text } = Typography;

interface revieworderProps {
  formInput: InputInterface;
  index: number;
  form: FormInstance;
  isCreateMode?: boolean;
  additionalData?: any;
  photoDetails?: any;
}

const ReviewPhotoOrder = (props: revieworderProps) => {
  const { photoDetails } = props;
  const [formDetails, setFormDetails] = useState<any>({});
  const [activeItem, setActiveItem] = useState<any>({});
  const [itemNumber, setItemNumber] = useState<number>();
  const [photoType, setPhotoType] = useState<string>('');
  const [paymentDetails, setPaymentDetails] = useState<any>({});
  const [userDetails, setUserDetails] = useState<any>({});
  const [openPhotoUploadModal, setOpenPhotoUploadModal] = useState<boolean>(false);
  const [activePrimaryTab, setActivePrimaryTab] = useState<string>('user-information');
  const [orderIdCopied, setOrderIdCopied] = useState<boolean>(false);
  const { items } = formDetails || [];
  const onDownload = async (fileUrl: any) => {
    if (fileUrl) {
      window.open(
        fileUrl?.startsWith('https') || !fileUrl ? fileUrl : `${BASE_IMAGE_URL}/${fileUrl}`
      );
    }
  };

  const getUserPhotos = (item: any) => {
    const photoArray = ['photo1', 'photo2', 'photo3', 'photo4'];

    return (
      <div style={{ display: 'flex', gap: '10px', flex: 'wrap' }}>
        {photoArray?.map((photo: any) => (
          <>
            <div
              key={item?.id}
              style={{
                display: 'flex',
                justifyContent: 'end',
                gap: '4px',
                padding: '6px',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                position: 'relative',
                height: '200px',
                overflow: 'scroll',
              }}
            >
              <Image
                width={200}
                src={
                  item[photo]?.url?.startsWith('https') || !item[photo]?.url
                    ? item[photo]?.url
                    : `${BASE_IMAGE_URL}/${item[photo]?.url}`
                }
                fallback={fallback}
              />
              <DownloadOutlined
                style={{
                  position: 'absolute',
                  fontSize: 18,
                  bottom: '8px',
                  right: '8px',
                  padding: '4px',
                  cursor: 'pointer',
                  backgroundColor: '#00000070',
                  borderRadius: '4px',
                  color: 'white',
                }}
                onClick={() => onDownload(item[photo]?.url)}
              />
            </div>
          </>
        ))}
      </div>
    );
  };

  const handlePhotoModal = (typeOfPhoto: string, item: any, itemNumber: number) => () => {
    setPhotoType(typeOfPhoto);
    setActiveItem(item);
    setItemNumber(itemNumber);
    setOpenPhotoUploadModal(!openPhotoUploadModal);
  };

  const getUser = async (userId: string) => {
    try {
      const entityData: any = await callApi(`${ORDER_USER}/${userId}`, 'GET');
      const { isSuccess, data } = entityData;
      if (!isSuccess) {
        // eslint-disable-next-line no-console
        console.error('Something went wrong, please try again');
      }
      setUserDetails(data?.data);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Something went wrong, please try again');
    }
  };

  const handleDownloadAll = (item: any) => {
    const photoArray = ['photo1', 'photo2', 'photo3', 'photo4'];

    const downloadWithDelay = async (url: string, filename: string) => {
      const a = document.createElement('a');
      a.href = url?.startsWith('https') || !url ? url : `${BASE_IMAGE_URL}/${url}`;
      a.download = filename;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      await new Promise(resolve => setTimeout(resolve, 1000));
    };

    const getFileExtension = (url: string) => {
      const parts = url.split('.');
      return parts[parts.length - 1].toLowerCase();
    };

    const downloadAll = async () => {
      for (let i = 0; i < photoArray.length; i++) {
        const photoUrl =
          item[photoArray[i]]?.url?.startsWith('https') || !item[photoArray[i]]?.url
            ? item[photoArray[i]]?.url
            : `${BASE_IMAGE_URL}/${item[photoArray[i]]?.url}`;
        if (photoUrl) {
          try {
            let filename = `photo${i + 1}`;
            const extension = getFileExtension(photoUrl);
            if (extension === 'jpg' || extension === 'png' || extension === 'pdf') {
              filename += `.${extension}`;
            }
            await downloadWithDelay(photoUrl, filename);
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Download failed for:', photoUrl, error);
          }
        }
      }
    };

    downloadAll();
  };

  const handleUpdateOrderStatus = async () => {
    try {
      const values = props.form.getFieldsValue(['status', 'trackingId']);
      await callApi(`${PHOTO_ORDER_APPLICATION}/${photoDetails?.id}`, 'PATCH', {
        status: values.status,
        trackingId: values.trackingId,
      });
      toastSuccess('Order status updated successfully');
      setFormDetails((prev: any) => ({
        ...prev,
        status: values.status ?? prev.status,
        trackingId: values.trackingId ?? prev.trackingId,
      }));
    } catch (error) {
      toastError('Failed to update order status');
    }
  };

  useEffect(() => {
    if (!photoDetails) {
      return;
    }
    const payment = {
      id: photoDetails?.id,
      expedite: photoDetails?.expedite, //price
      photoEnhancement: photoDetails?.photoEnhancement, //price
      pricingPlan: photoDetails?.pricingPlan, //discounted price
      convenienceFee: photoDetails?.convenienceFee,
      payableOfferPrice: photoDetails?.payableOfferPrice, // totalDiscountedprice + convenience fees
      orderId: photoDetails?.orderId,
      trackingId: photoDetails?.trackingId,
      pickupAddress: photoDetails?.pickupAddress,
      expectedDate: photoDetails?.expectedDate,
      appliedCoupon: photoDetails?.appliedCoupon,
      couponDiscount: photoDetails?.couponDiscount,
      paymentDate: photoDetails?.paymentDetails?.paymentDate,
      paymentMethod: photoDetails?.paymentDetails?.paymentMethod,
      orderDate: photoDetails?.createdAt,
      paymentDetails: formDetails?.paymentDetails || {},
      address: photoDetails?.address,
      items: photoDetails?.items || [],
    };
    getUser(photoDetails?.userId);
    setPaymentDetails(payment);
    setFormDetails(photoDetails);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photoDetails]);

  const currentUserRole: any = getRole();

  const NAVY = 'black';

  const PrintField = ({
    label,
    value,
    style,
  }: {
    label: string;
    value?: React.ReactNode;
    style?: React.CSSProperties;
  }) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', ...(style || {}) }}>
      <span
        style={{
          fontSize: '15px',
          fontWeight: 400,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: '#53657f',
          lineHeight: 1.2,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: '16px',
          fontWeight: 600,
          color: '#0f172a',
          lineHeight: 1.35,
        }}
      >
        {value || '—'}
      </span>
    </div>
  );

  const orderCardStyle: React.CSSProperties = {
    width: '100%',
    borderRadius: '10px',
    border: '1px solid #e2e8f0',
    background: '#fff',
    padding: '18px 20px',
    marginBottom: '18px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '16px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    color: NAVY,
    marginBottom: '12px',
    paddingBottom: '10px',
    borderBottom: '1px solid #e2e8f0',
    lineHeight: 1.2,
  };

  const fieldGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    columnGap: '28px',
    rowGap: '14px',
  };

  const primaryTabButtonBase: React.CSSProperties = {
    flex: '0 0 auto',
    padding: '7px 16px',
    border: '0',
    borderRadius: '7px',
    background: 'transparent',
    color: '#60758d',
    fontWeight: 600,
    fontSize: 'medium',
    letterSpacing: '0.01em',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
  };

  const primaryTabButtonActive: React.CSSProperties = {
    background: '#ffffff',
    color: '#004195',
    boxShadow: '0 1px 2px rgba(16, 24, 40, 0.08), 0 1px 3px rgba(16, 24, 40, 0.06)',
  };

  const formatDate = (dateVal: any) => {
    if (!dateVal) return '—';
    const sd = new Date(dateVal);
    const day = sd.getDate();
    const monthName = sd.toLocaleString('default', { month: 'short' });
    const year = sd.getFullYear();
    return `${day}/${monthName}/${year}`;
  };

  const normalizeStatus = (status?: string) =>
    String(status || '')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');

  const getUserTabKey = (item: any, idx: number) => `user-${item?.id ?? idx}`;

  const paymentStatusInner =
    paymentDetails?.paymentDetails?.paymentStatus ||
    photoDetails?.paymentDetails?.paymentStatus ||
    photoDetails?.paymentStatus ||
    '';

  const orderIdValue = photoDetails?.orderId || photoDetails?.id || '--';

  return (
    <>
      <div
        style={{
          position: 'relative',

          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          padding: '16px 20px',
          borderRadius: '12px',
          border: '1px solid #e2e8f0',
          backgroundColor: '#ffffff',
          marginBottom: '20px',
          gap: '16px',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '2px',
              position: 'absolute',
              backgroundColor: 'aliceblue',
              border: '1px solid rgb(226, 232, 240)',
              padding: '0px 8px',
              borderRadius: '4px',
              top: '-30px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Text
                style={{
                  fontSize: '20px',
                  fontWeight: 700,
                  color: '#0f172a',
                  margin: 0,
                  lineHeight: 1.2,
                  letterSpacing: '-0.01em',
                }}
              >
                {orderIdValue}
              </Text>
              <div style={{ position: 'relative', display: 'inline-flex' }}>
                <button
                  type="button"
                  title={orderIdCopied ? 'Copied!' : 'Copy Order ID'}
                  onClick={() => {
                    if (orderIdValue && orderIdValue !== '--' && navigator?.clipboard) {
                      navigator.clipboard
                        .writeText(String(orderIdValue))
                        .then(() => {
                          setOrderIdCopied(true);
                          window.setTimeout(() => setOrderIdCopied(false), 1600);
                        })
                        .catch(() => {});
                    }
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    border: 'none',
                    background: orderIdCopied ? '#e6f4ea' : 'transparent',
                    cursor: 'pointer',
                    color: orderIdCopied ? '#1e8e3e' : '#0b63d1',
                    padding: 0,
                    transition: 'background-color 0.15s ease, color 0.15s ease',
                  }}
                  onMouseEnter={e => {
                    if (!orderIdCopied) e.currentTarget.style.backgroundColor = '#eef2ff';
                  }}
                  onMouseLeave={e => {
                    if (!orderIdCopied) e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {orderIdCopied ? (
                    <CircleCheckBig size={18} strokeWidth={2.25} />
                  ) : (
                    <Clipboard size={18} strokeWidth={2} />
                  )}
                </button>
                {orderIdCopied && (
                  <span
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 4px)',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      whiteSpace: 'nowrap',
                      backgroundColor: '#0f172a',
                      color: '#ffffff',
                      fontSize: '11px',
                      fontWeight: 600,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      boxShadow: '0 4px 10px rgba(15, 23, 42, 0.2)',
                      zIndex: 50,
                      pointerEvents: 'none',
                      letterSpacing: '0.01em',
                    }}
                  >
                    Copied!
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Primary Tabs Navigation (pill style, right) */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '2px',
              overflowX: 'auto',
              padding: '4px',
              backgroundColor: '#eef1f5',
              border: '0',
              borderRadius: '10px',
              scrollbarWidth: 'none',
            }}
          >
            {/* Tab 1: User & Payment */}
            <button
              type="button"
              onClick={() => setActivePrimaryTab('user-information')}
              style={{
                ...primaryTabButtonBase,
                ...(activePrimaryTab === 'user-information' ? primaryTabButtonActive : {}),
              }}
            >
              <User size={15} strokeWidth={2.25} />
              User & Payment
            </button>

            {/* Tab: Members (all users stacked, hidden from Accountant) */}
            {currentUserRole !== 'Accountant' && items?.length > 0 && (
              <button
                type="button"
                onClick={() => setActivePrimaryTab('members')}
                style={{
                  ...primaryTabButtonBase,
                  padding: '7px 12px 7px 16px',
                  gap: '8px',
                  ...(activePrimaryTab === 'members' ? primaryTabButtonActive : {}),
                }}
              >
                <IdCard size={15} strokeWidth={2.25} />
                Members
              </button>
            )}
          </div>

          {/* Order Status inline bar (moved out of the Order Status tab) */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '16px',
              flexShrink: 0,
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',

                padding: '0 16px',
                borderRadius: '8px',
                border: '1px solid #86efac',
                overflow: 'hidden',
                backgroundColor: '#f0fdf4',
                color: '#15803d',
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(34, 197, 94, 0.15)',
              }}
            >
              <Text style={{ fontSize: '16px', fontWeight: '600', minWidth: '80px' }}>
                Application Status:
              </Text>
              <Form.Item
                name="status"
                style={{ marginBottom: 0, minHeight: '53px', padding: '4px 24px' }}
              >
                <Select
                  style={{ width: 300, fontSize: '16px' }}
                  showSearch
                  optionFilterProp="label"
                  optionLabelProp="displayLabel"
                  filterOption={(input, option) => {
                    const opt = option as unknown as Record<string, any>;
                    const label = String(opt?.searchLabel ?? opt?.name ?? opt?.value ?? '');
                    return label.toLowerCase().includes(input.toLowerCase());
                  }}
                  value={formDetails?.status || ''}
                  onChange={value => {
                    setFormDetails((prev: any) => ({ ...prev, status: value }));
                  }}
                  placeholder="Select status"
                  menuItemSelectedIcon={null}
                  options={(() => {
                    const currentSelectedValue = formDetails?.status || '';
                    return statusOption.map((opt: any) => {
                      const optionValue = String(opt?.value ?? opt?.name ?? '');
                      const searchLabel = String(opt?.label ?? opt?.name ?? optionValue ?? '');
                      const isSelected = optionValue === String(currentSelectedValue);
                      return {
                        ...opt,
                        searchLabel,
                        displayLabel: searchLabel,
                        label: (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '2px 0',
                              fontSize: '14px',
                              fontWeight: isSelected ? 600 : 500,
                              color: isSelected ? '#075dc5' : '#1f2937',
                              lineHeight: 1.2,
                              width: '100%',
                            }}
                          >
                            <div
                              style={{
                                width: '16px',
                                height: '16px',
                                borderRadius: '3px',
                                border: isSelected ? '2px solid #075dc5' : '1.5px solid #94a3b8',
                                backgroundColor: isSelected ? '#075dc5' : '#ffffff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transition: 'background-color 0.15s ease, border-color 0.15s ease',
                                boxShadow: isSelected ? '0 1px 3px rgba(7, 93, 197, 0.3)' : 'none',
                              }}
                            >
                              {isSelected && (
                                <Check
                                  size={11}
                                  strokeWidth={3.5}
                                  color="#ffffff"
                                  style={{ display: 'block' }}
                                />
                              )}
                            </div>
                            <span
                              style={{
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {searchLabel}
                            </span>
                          </div>
                        ),
                      };
                    });
                  })()}
                  size="middle"
                />
              </Form.Item>
              <Text style={{ fontSize: '16px', fontWeight: '600', minWidth: '80px' }}>
                Tracking Id:
              </Text>
              <Form.Item
                name="trackingId"
                style={{ marginBottom: 0, minHeight: '53px', padding: '4px 24px' }}
              >
                <Input
                  style={{ width: 260, fontSize: '16px' }}
                  placeholder="Enter tracking id"
                  size="middle"
                />
              </Form.Item>
              <Button
                type="primary"
                onClick={handleUpdateOrderStatus}
                style={{
                  fontSize: '13px',
                  height: '32px',
                  padding: '0 18px',
                  backgroundColor: '#586f80',
                  borderColor: '#586f80',
                  borderRadius: '4px',
                  fontWeight: 600,
                }}
              >
                Update
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* TAB CONTENT PANELS */}
      <div>
        {/* Panel: User & Payment (merged) */}
        {activePrimaryTab === 'user-information' && (
          <>
            <UserDetails userDetails={userDetails} paymentDetails={paymentDetails} columns={4} />
            <PaymentDetails
              formDetails={photoDetails}
              paymentDetails={paymentDetails}
              userName={userDetails.name}
              userEmail={userDetails.email}
              hidePaymentVoucher
            />
          </>
        )}

        {/* Panel: Members (all users stacked, hidden from Accountant) */}
        {activePrimaryTab === 'members' &&
          currentUserRole !== 'Accountant' &&
          items?.map((item: any, itemNumber: number) => (
            <div key={getUserTabKey(item, itemNumber)}>
              <div style={orderCardStyle}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px',
                    marginTop: '10px',
                    paddingBottom: '10px',
                    borderBottom: '1px solid #e2e8f0',
                    position: 'relative',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: -40,
                      padding: '0px 10px',
                      fontSize: 'medium',
                      fontWeight: '600',
                      backgroundColor: 'aliceblue',
                      border: '1px solid rgb(226, 232, 240)',
                    }}
                  >
                    User {itemNumber + 1}
                  </div>
                  <PrintField label="Document Type" value={item.documentType || '--'} />
                  <Space>
                    <InvertFillButton
                      variant="white"
                      fontSize="base"
                      fontWeight="semibold"
                      onClick={handlePhotoModal('Processed Grid Photo', item, itemNumber)}
                    >
                      + Add Processed Photo
                    </InvertFillButton>
                  </Space>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {getUserPhotos(item)}

                  <InvertFillButton
                    variant="blue"
                    fontSize="base"
                    fontWeight="semibold"
                    style={{
                      alignSelf: 'flex-start',
                      marginTop: '20px',
                    }}
                    onClick={() => handleDownloadAll(item)}
                  >
                    Download All Documents
                  </InvertFillButton>
                </div>
              </div>
            </div>
          ))}
      </div>

      <PhotoUploadModal
        photoType={photoType}
        activeItem={activeItem}
        itemNumber={itemNumber}
        formDetails={formDetails}
        openPhotoUploadModal={openPhotoUploadModal}
        setOpenPhotoUploadModal={setOpenPhotoUploadModal}
      />
    </>
  );
};

export default ReviewPhotoOrder;
